import moment from "moment";
import { ALL_DATES_END, ALL_DATES_START } from "./dateRangePresets";

// Shared date math for the "lastWeek" / "thisMonth" / ... presets. This is the
// single definition of what each preset resolves to: DateRangePicker (Reports)
// and getDateFilter (src/utils/index.ts, used by Commission) both delegate
// here, so a preset can never mean two different ranges in two places.
export const resolveDateRangePreset = (
  preset: string,
  customStart: Date | null = null,
  customEnd: Date | null = null
): { start: moment.Moment | null; end: moment.Moment | null } | null => {
  const today = moment().endOf("day");
  let start: moment.Moment | null = null;
  let end: moment.Moment | null = today.clone();

  switch (preset) {
    // Mon-Sun, not moment's locale-default (Sunday-start) week — isoWeek
    // keeps this consistent regardless of the app's global moment locale.
    case "thisWeek":
      start = today.clone().startOf("isoWeek");
      end = today.clone().endOf("isoWeek");
      break;
    case "lastWeek":
      start = today.clone().subtract(1, "week").startOf("isoWeek");
      end = today.clone().subtract(1, "week").endOf("isoWeek");
      break;
    case "thisMonth":
      start = today.clone().startOf("month");
      end = today.clone().endOf("month");
      break;
    case "lastMonth":
      start = today.clone().subtract(1, "month").startOf("month");
      end = today.clone().subtract(1, "month").endOf("month");
      break;
    // The last N *complete* calendar months — always ends at the end of last
    // month, regardless of N, not "today" (that would include the partial
    // current month).
    case "last3Months":
      start = today.clone().subtract(3, "months").startOf("month");
      end = today.clone().subtract(1, "month").endOf("month");
      break;
    case "last6Months":
      start = today.clone().subtract(6, "months").startOf("month");
      end = today.clone().subtract(1, "month").endOf("month");
      break;
    // Unlike last3Months/last6Months, this is a rolling trailing-12-month
    // window ending *today* (the common LTM/"last twelve months" reporting
    // convention), not the last 12 complete calendar months — so no
    // startOf("month") here.
    case "last12Months":
      start = today.clone().subtract(12, "months");
      break;
    case "thisCalendarYear":
      start = today.clone().startOf("year");
      end = today.clone().endOf("year");
      break;
    case "lastCalendarYear":
      start = today.clone().subtract(1, "year").startOf("year");
      end = today.clone().subtract(1, "year").endOf("year");
      break;
    case "next30Days":
      start = today.clone();
      end = today.clone().add(30, "days");
      break;
    case "next60Days":
      start = today.clone();
      end = today.clone().add(60, "days");
      break;
    case "next90Days":
      start = today.clone();
      end = today.clone().add(90, "days");
      break;
    // The next N *complete* calendar months — always starts at the 1st of
    // next month, regardless of N.
    case "next3Months":
      start = today.clone().add(1, "month").startOf("month");
      end = today.clone().add(3, "months").endOf("month");
      break;
    case "next6Months":
      start = today.clone().add(1, "month").startOf("month");
      end = today.clone().add(6, "months").endOf("month");
      break;
    case "next12Months":
      start = today.clone().add(1, "month").startOf("month");
      end = today.clone().add(12, "months").endOf("month");
      break;
    case "last30Days":
      start = today.clone().subtract(30, "days");
      break;
    case "last60Days":
      start = today.clone().subtract(60, "days");
      break;
    case "last90Days":
      start = today.clone().subtract(90, "days");
      break;
    case "allDates":
      // "Include all Dates" hides both date pickers, so the range is resolved
      // here rather than coming from the UI. See ALL_DATES_START/END for why
      // this is an explicit wide range instead of "send no dates".
      start = moment(ALL_DATES_START).startOf("day");
      end = moment(ALL_DATES_END).endOf("day");
      break;
    case "custom":
      start = customStart ? moment(customStart) : null;
      end = customEnd ? moment(customEnd) : null;
      break;
    default:
      // Unrecognized preset key — let the caller decide how to handle it.
      return null;
  }

  return { start, end };
};
